/**
 * ERC Power Pin Validator Adapter for DRC System
 *
 * This validator checks for power input pins that are not properly connected to power.
 * It uses a component-centric approach:
 *
 * 1. Iterate over components, detect power input pins (by type or name pattern)
 * 2. For each power input pin:
 *    - If not connected to any net → missing power error
 *    - If connected to a net → verify the net is a power net:
 *      - Has a Power Net Portal (qualifies as power)
 *      - Has a Ground element (qualifies as power)
 *      - Has a power driver (POWER_OUT pin, or power source component's positive terminal)
 *      - Has an inductor whose other pin connects to a power net (inductor pass-through, e.g. LC filter)
 *    - If net is not a power net → missing power error
 *
 * Feature-gated via DrcEnabledCheckers.ErcPowerPinValidator
 */
import { createTUID, elementMatchesPartType, isGroundElement, ReviewType } from "@buildwithflux/models";
import { getProblemTypeKeyFromReviewType } from "../../drc/types";
import { ElectricalPinType } from "../types";
import { getPinInfo, getPinsForNet, createErcProblem, formatPinReference, isPortalOrGroundElement, isLikelyPositivePin, isPowerSourcePartType, } from "./ercUtils";
/**
 * Prefix patterns for power supply pin names (case-insensitive).
 * Pins starting with any of these are treated as power inputs.
 */
const POWER_PIN_PREFIXES = ["VDD", "AVDD", "DVDD", "PVDD", "VCORE", "VIO", "VDDIO", "VSUP", "VBAT", "VBUS", "HV"];
/**
 * Exact match patterns for power supply pin names (case-insensitive).
 * These are names not covered by the prefix patterns above.
 */
const POWER_PIN_EXACT_MATCHES = ["VCC", "VIN", "VANA", "VDIG", "VS", "VP", "VPP", "VAA", "VBB"];
/**
 * Checks if a pin name matches common power supply naming patterns.
 * This helps detect power pins even when the pin type is not explicitly set.
 */
function isPowerPinName(name) {
    if (!name)
        return false;
    const upperName = name.toUpperCase().trim();
    // Check prefix matches
    for (const prefix of POWER_PIN_PREFIXES) {
        if (upperName.startsWith(prefix)) {
            return true;
        }
    }
    // Check exact matches
    return POWER_PIN_EXACT_MATCHES.includes(upperName);
}
/**
 * Checks if a pin is a power input (by type or name pattern).
 */
function isPowerInputPin(pin) {
    const isPowerInputByType = pin.pinType === ElectricalPinType.POWER_IN;
    if (isPowerInputByType)
        return true;
    // Don't treat driver pins as power inputs even if their name matches a power pattern
    if (canDrivePowerInput(pin))
        return false;
    const isPowerInputByName = isPowerPinName(pin.pinName) || isPowerPinName(pin.pinNumber);
    return isPowerInputByName;
}
/**
 * Checks if a pin can drive a power input.
 * A pin can drive power if:
 * 1. Its electrical type is OUTPUT, POWER_OUT, or BIDIRECTIONAL
 * 2. It's a likely positive terminal on a power source component (Battery, Power Source, etc.)
 */
function canDrivePowerInput(pin) {
    const isDriverByType = pin.pinType === ElectricalPinType.OUTPUT ||
        pin.pinType === ElectricalPinType.POWER_OUT ||
        pin.pinType === ElectricalPinType.BIDIRECTIONAL;
    if (isDriverByType)
        return true;
    if (isPowerSourcePartType(pin.elementPartType) && isLikelyPositivePin(pin.pinName, pin.pinNumber)) {
        return true;
    }
    return false;
}
/**
 * Checks if a net qualifies as a power net. A net is a power net if it has:
 * - A Power Net Portal
 * - A Ground element
 * - A power driver (POWER_OUT pin or power source component's positive terminal)
 * - An inductor whose other pin connects to a power net (inductor pass-through, e.g. LC filter)
 */
function isPowerNet(inputs, netHash, terminalToNet, visitedNets = new Set()) {
    if (visitedNets.has(netHash))
        return false;
    visitedNets.add(netHash);
    const netTerminals = inputs.netMap[netHash];
    if (!netTerminals)
        return false;
    const pins = getPinsForNet(inputs, netTerminals);
    for (const pin of pins) {
        const element = inputs.elements[pin.elementUid];
        if (!element)
            continue;
        // Power Net Portal qualifies the net as a power net
        if (element.part_version_data_cache?.name === "Power Net Portal") {
            return true;
        }
        // Ground element qualifies the net as a power net
        if (isGroundElement(element)) {
            return true;
        }
        // Power driver (POWER_OUT, regulator output, etc.) qualifies the net
        if (canDrivePowerInput(pin)) {
            return true;
        }
        // Inductor pass-through: if the other pin of the inductor connects to a power net
        const terminals = element.part_version_data_cache?.terminals;
        if (terminals && elementMatchesPartType(element, "Inductor")) {
            const otherTerminalId = getOtherInductorTerminal(pin.elementUid, terminals, pin.terminalUid);
            if (otherTerminalId) {
                const otherNetHash = terminalToNet[otherTerminalId];
                if (otherNetHash && isPowerNet(inputs, otherNetHash, terminalToNet, visitedNets)) {
                    return true;
                }
            }
        }
    }
    return false;
}
/**
 * Gets the terminal ID of the inductor's other pin (inductors typically have 2 pins).
 */
function getOtherInductorTerminal(elementUid, terminals, currentTerminalUid) {
    const terminalUids = Object.keys(terminals);
    const other = terminalUids.find((uid) => uid !== currentTerminalUid);
    return other ? createTUID([elementUid, other]) : null;
}
/**
 * Validator that checks for power pin connection issues.
 */
export class ErcPowerPinValidator {
    reviewType = ReviewType.DesignRuleCheckErcPowerPin;
    problemTypeKey = getProblemTypeKeyFromReviewType(this.reviewType);
    defaultHidden = false;
    async checkForProblems(inputs) {
        const foundProblems = [];
        // Build terminal → net mapping (only terminals that are connected appear in netMap)
        const terminalToNet = {};
        for (const [netHash, terminals] of Object.entries(inputs.netMap)) {
            for (const terminalId of Object.keys(terminals)) {
                terminalToNet[terminalId] = netHash;
            }
        }
        // Iterate over components, find power input pins
        for (const [elementUid, element] of Object.entries(inputs.elements)) {
            // Skip portals and ground — they are power sources, not consumers
            if (isPortalOrGroundElement(element)) {
                continue;
            }
            const terminals = element.part_version_data_cache?.terminals;
            if (!terminals)
                continue;
            for (const terminalUid of Object.keys(terminals)) {
                const terminalId = createTUID([elementUid, terminalUid]);
                const pin = getPinInfo(inputs, terminalId);
                if (!pin || !isPowerInputPin(pin))
                    continue;
                const netHash = terminalToNet[terminalId];
                const pinIdentifier = formatPinReference(pin);
                // Not connected to any net → missing power
                if (netHash === undefined) {
                    foundProblems.push(createErcProblem(this.problemTypeKey, `${this.problemTypeKey}_${pin.elementUid}_${pin.terminalUid}`, `Power input not connected: ${pinIdentifier}`, [pin.elementUid], [pinIdentifier], undefined, { ercNet: "—" }));
                    continue;
                }
                // Connected to a net — verify it's a power net
                if (!isPowerNet(inputs, netHash, terminalToNet)) {
                    const netName = inputs.netNameByNetId?.[netHash] ?? netHash;
                    foundProblems.push(createErcProblem(this.problemTypeKey, `${this.problemTypeKey}_${pin.elementUid}_${pin.terminalUid}`, `Power input not driven: ${pinIdentifier}`, [pin.elementUid], [pinIdentifier], undefined, { ercNet: netName }));
                }
            }
        }
        return {
            error: false,
            problemTypeKey: this.problemTypeKey,
            foundProblems,
        };
    }
}
//# sourceMappingURL=ErcPowerPinValidator.js.map